import type { Translations } from '../Translation';
import type User from '../user/User';

export enum Access {
  intraClient = 0,
  restricted = 1,
}

export const AccessTypeKeys = {
  [Access.restricted]: 'documents:access.restricted',
  [Access.intraClient]: 'documents:access.client',
} as const;

export type ClientFormTag = {
  id: string;
  type: ClientTagType;
  synchronized: boolean;
  translations: Translations<'name'>;
  formSectionId?: string | null;
  automationId?: string;
  role?: number;
  users?: User[];
  criteria?: string;
  requiresAction?: boolean | null;
  sortOrder?: number | null;
  /** User-provided rule group title, carried from criteria policy metadata into the editor. */
  name?: string | null;
};

export type ClientUserGrouping = {
  id: string;
  translations: Translations<'name'>;
  type: ClientTagType;
  users: (User & { tagIds?: string[] })[];
  formSectionId: string | null;
};

export type ClientTagRequest = {
  type: ClientTagType;
  translations: Translations<'name'>;
};

export type ClientTagUpdateRequest = {
  translations: Translations<'name'>;
};

export type ClientTagFilter = {
  types?: ClientTagType[];
  pageSize?: number;
  pageNumber?: number;
  sortBy?: string;
  includeItemIds?: boolean;
  search?: string;
};

export type ClientTagResponse = {
  id: string;
  type: ClientTagType;
  clientId: string;
  translations: Translations<'name'>;
  itemsCount: number;
  itemIds: string[];
  formSectionId: string | null;
};

export type TargetTag = {
  id: string;
  type: ClientTagType;
  translations: Translations<'name'>;
};

export type TargetTagUpdateRequest = {
  type: ClientTagType;
  tagIds: string[];
};

export const nameFor = (
  tag: Pick<ClientTagResponse, 'translations'> | Pick<TargetTag, 'translations'>,
  language: string,
  fallbackLanguage = 'en',
): string => {
  const translations = tag.translations as Record<string, { name?: string } | undefined>;
  return translations[language]?.name ?? translations[fallbackLanguage]?.name ?? '';
};

export enum ClientTagType {
  ContactGroup = 0,
  UserGroup = 1,
  UserPosition = 2,
  UserDepartment = 3,
  Tag = 4,
}

export const ClientTagTypeKeys = {
  [ClientTagType.ContactGroup]: 'common:user-group-types.contact',
  [ClientTagType.UserGroup]: 'common:user-group-types.group',
  [ClientTagType.UserPosition]: 'common:user-group-types.position',
  [ClientTagType.UserDepartment]: 'common:user-group-types.department',
  [ClientTagType.Tag]: 'common:user-group-types.tag',
} as const;

export type ClientTagWithItems<TItem> = ClientTagResponse & {
  items: TItem[];
};

export type BulkTagRequest = {
  selectedAll: boolean;
  excludedIds: string[];
  selectedIds: string[];
  operation: BulkTagRequestOperation;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  itemFilters: Record<string, any>;
};

export enum BulkTagRequestOperation {
  Add = 0,
  Remove = 1,
}
